import { useEffect, useState } from "react";

import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { Button } from "@/design-system/Button";
import { Select } from "@/design-system/Select";
import { ModalBodySpinner } from "@/design-system/Spinner";
import { useBool } from "@/hooks/useBool";
import { useTranslation } from "@/translations";

import { TicketStatus } from "./TicketStatus";

type Status = {
  id: string;
  description: string;
  labelColor: string;
  defaultStatusId: string;
};

type TicketStatusChangeModalProps = ModalBaseProps & {
  targetStatus: Status;
  possibleStatuses: Status[];
  onSubmit?: (status: Status) => Promise<void>;
};

export function TicketStatusChangeModal({
  isOpened,
  onOpenChange,
  onSubmit,
  targetStatus,
  possibleStatuses,
}: TicketStatusChangeModalProps): React.ReactNode {
  const { t } = useTranslation();
  const [submitting, submittingHandlers] = useBool();
  const [status, setStatus] = useState<Status>();

  useEffect(() => {
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    setStatus(targetStatus);
  }, [targetStatus]);

  async function close(submit: boolean) {
    if (submitting) {
      return;
    }

    submittingHandlers.setTrue();

    try {
      if (submit && status) {
        await onSubmit?.(status);
      } else {
        onOpenChange(false);
      }
    } finally {
      submittingHandlers.setFalse();
    }
  }

  return (
    <Modal.Root
      data-testid="quick-repair-request-status-modal"
      size="sm"
      isNested
      {...{ isOpened, onOpenChange }}
      title={t("component.status-change-modal.title-after-response")}
      description={t("component.status-change-modal.description")}
    >
      <Modal.Body>
        <Select
          width="dialog"
          placeholder={t("model.repair-request.status-change-label")}
          selected={status}
          disabled={submitting}
          items={possibleStatuses}
          onChange={setStatus}
          keySelector={(x) => x.id}
          renderOption={(option) => <TicketStatus {...option} variant="dot" />}
          usePortal
        />
        {submitting && <ModalBodySpinner inset="compact" />}
      </Modal.Body>
      {!submitting && (
        <Modal.Actions>
          <Button variant="outline" onClick={() => close(false)} data-testid="quick-repair-request-status-modal-reject">
            {t("common.action.no-thanks")}
          </Button>
          <Button onClick={() => close(true)} data-testid="quick-repair-request-status-modal-apply">
            {t("component.status-change-modal.btn.confirm")}
          </Button>
        </Modal.Actions>
      )}
    </Modal.Root>
  );
}
